import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
  DATA_CONFIG,
  SOCIAL_KEYS,
  type SocialKey,
} from '../../injection-tokens';
import { NAV_GROUPS } from '../../site-shell/nav-data';

interface FooterLink {
  name: string;
  url: string;
}

// The two lesson spokes the header nav does not list, appended to the
// site map's derived list — they are pages a site map should name, but
// spokes stay out of the top nav per spec §5 (spec §15.2, 2026-09-14,
// replacing the removed For-students group).
const SPOKE_LINKS: readonly FooterLink[] = [
  { name: 'What I Teach', url: '/bass-lessons/what-i-teach' },
  { name: 'Zoom Setup', url: '/bass-lessons/zoom-setup' },
];

interface FooterSocial {
  key: SocialKey;
  label: string;
  href: string;
  iconUrl: string;
}

@Component({
  selector: 'app-footer',
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent {
  private readonly dataConfig = inject(DATA_CONFIG);

  // Flat "site map" list, sourced from the shared NAV_GROUPS so the footer
  // stays in sync with the top bar + mobile drawer (and the route-reachability
  // guard). A flat wrapped list flows predictably at every width; panel groups
  // contribute every link across their sub-sections.
  protected readonly siteLinks: FooterLink[] = [
    ...NAV_GROUPS.flatMap((g) => {
      if (g.kind === 'link') {
        return g.href ? [{ name: g.label, url: g.href }] : [];
      }
      if (g.kind === 'flat') {
        return (g.links ?? []).map((l) => ({ name: l.label, url: l.href }));
      }
      return (g.sections ?? []).flatMap((s) =>
        s.links.map((l) => ({ name: l.label, url: l.href })),
      );
    }),
    ...SPOKE_LINKS,
  ];

  protected readonly phoneDisplay = this.dataConfig.phone;
  protected readonly phoneHref = this.dataConfig.phoneHref;
  protected readonly emailDisplay = this.dataConfig.email;
  protected readonly emailHref = this.dataConfig.emailHref;

  protected readonly socialLinks: FooterSocial[] = SOCIAL_KEYS.map((key) => ({
    key,
    label: this.dataConfig[key].displayText,
    href: this.dataConfig[key].link,
    iconUrl: `url('/social-icons/${key}.svg')`,
  }));

  private readonly currentYear = new Date().getFullYear();
  private readonly originalYear = 2025;

  protected readonly copyrightYear =
    this.currentYear === this.originalYear
      ? `${this.currentYear}`
      : `${this.originalYear}-${this.currentYear}`;
}
